import { useEffect, useState } from "react";
import "./AppEmployeeRecordPayrollEmployee.css";
import { 
    CheckContractor,
    EmployeeRecord,
    EmployeeType,
    CheckBankAccount,
    CheckWorkplace,
    Address,
    getCurrentCheckCompanyId,
    convertDateToCheckYYYYMMDD,
    CheckBankAccountStatus,
    CHECK_BANK_ACCOUNT_STATUS,
    CheckResponseError, 
    convertTextareaToAddress,
    CheckForm,
    CheckFormStatus} from "winslow-core";
import { Alert, AlertDescription, Button, Input, Radio } from "../../../ds2/components";
import { AppPayBankAccount } from "../app-payroll/AppPayBankAccount";
import { apiCheckGetContractor, apiCheckListContractorBankAccounts, apiCheckCreateWorkplace, apiCheckCreateContractor, apiCheckListContractorForms, apiCheckGetContractorForm, apiCheckSubmitContractorForm, apiCheckUpdateContractor, apiCheckUpdateContractorSSN, apiCheckUpdateContractorEIN } from "../../../services/PayrollService";
import { Plus } from "lucide-react";
import { ActionLink } from "../../../ds2/components/ActionLink";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
import { Icon } from "../../../ds2/components";
import { Edit } from "lucide-react";
import "../app-payroll/AppPayBankAccounts.css";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/features/appData";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import { isCheckError } from "winslow-core";
import { erGetCPValue } from "./employeeRecordUtils";
import { apiUpdateEmployeeRecord } from "../../../services/EmployeeRecordService";
import { AppCheckErrorMessage } from "../app-payroll/AppCheckErrorMessage";
import { ModalComponentWrapper } from "../../shared/ModalComponentWrapper";
import { AppEmployeeRecordCheckForm } from "./AppEmployeeRecordCheckForm";
import { AppCheckContractorStatus } from "../app-payroll/AppCheckContractorStatus";
import { AppPayContractorOnboardingStatus } from "../app-payroll/AppPayContractorOnboardingStatus";

type AppEmployeeRecordPayrollContractorProps = {
    employeeRecord: EmployeeRecord | null;
    checkContractor: CheckContractor | null;
    onCheckContractorCreated?: (checkContractor: CheckContractor) => void;
    onCheckContractorUpdated?: () => void;
};

export const AppEmployeeRecordPayrollContractor = ({ 
    employeeRecord,
    checkContractor,
    onCheckContractorCreated,
    onCheckContractorUpdated
}: AppEmployeeRecordPayrollContractorProps) => {
    const storeUser = useSelector(selectUser);
    const isVendor = employeeRecord?.employeeType === EmployeeType.US_VENDOR;
    const canSwitchIdType = employeeRecord?.employeeType === EmployeeType.US_CONTRACTOR || employeeRecord?.employeeType === EmployeeType.INT_CONTRACTOR;

    const [showBankAccountForm, setShowBankAccountForm] = useState<boolean>(false);
    const [creating, setCreating] = useState<boolean>(false);
    const [loadingBankAccounts, setLoadingBankAccounts] = useState<boolean>(false);

    const [ssnOrEin, setSSNOrEin] = useState<string>("");
    const [idType, setIdType] = useState<"SSN" | "EIN">("SSN"); // For contractors: SSN or EIN
    const [bankAccounts, setBankAccounts] = useState<CheckBankAccount[]>([]);
    const [selectedBankAccountId, setSelectedBankAccountId] = useState<string | null>(null);
    const [editingBankAccount, setEditingBankAccount] = useState<CheckBankAccount | null>(null);

    const [address, setAddress] = useState<Address | null>(null);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [originalSSNOrEin, setOriginalSSNOrEin] = useState<string>("");
    const [hasInitializedSSNOrEin, setHasInitializedSSNOrEin] = useState<boolean>(false);

    const [checkResponseError, setCheckResponseError] = useState<CheckResponseError | undefined>(undefined);
    const [showOnboardingStatusModal, setShowOnboardingStatusModal] = useState<boolean>(false);
    const [checkFormStatuses, setCheckFormStatuses] = useState<CheckFormStatus[]>([]);
    const [loadingForms, setLoadingForms] = useState<boolean>(false);
    const [submittingForm, setSubmittingForm] = useState<boolean>(false);
    const [selectedFormStatus, setSelectedFormStatus] = useState<CheckFormStatus | null>(null);
    const [selectedForm, setSelectedForm] = useState<CheckForm | null>(null);
    const [showFormModal, setShowFormModal] = useState<boolean>(false);
    const [loadingFormDetails, setLoadingFormDetails] = useState<boolean>(false);
    const [ssnInputFocused, setSSNInputFocused] = useState<boolean>(false);

    const checkCompanyId = getCurrentCheckCompanyId(storeUser);

    useEffect(() => {
        if (employeeRecord) {
            const cpLongText = erGetCPValue(employeeRecord, "system-address") || "";
            const convertedAddress = cpLongText ? convertTextareaToAddress(cpLongText) : null;
            setAddress(convertedAddress);
        }
    }, [employeeRecord]);

    useEffect(() => {
        if (checkContractor?.id) {
            loadBankAccounts();
            setHasChanges(false);
            if (!hasInitializedSSNOrEin) {
                if (isVendor) {
                    // For vendors, always use EIN
                    const value = checkContractor.ein || "";
                    setOriginalSSNOrEin(value);
                    setSSNOrEin(value);
                    setIdType("EIN");
                } else {
                    // If business_name is not null, show EIN
                    if (checkContractor.business_name !== null && checkContractor.ein) {
                        setOriginalSSNOrEin(checkContractor.ein);
                        setSSNOrEin(checkContractor.ein);
                        setIdType("EIN");
                    } else if (checkContractor.ein) {
                        // EIN exists but no business_name
                        setOriginalSSNOrEin(checkContractor.ein);
                        setIdType("EIN");
                    } else if (checkContractor.ssn_last_four) {
                        // SSN exists
                        setOriginalSSNOrEin(checkContractor.ssn_last_four);
                        setIdType("SSN");
                    } else {
                        // Neither exists
                        setOriginalSSNOrEin("");
                        setIdType("SSN"); // Default to SSN if neither exists
                    }
                }
                setHasInitializedSSNOrEin(true);
            }
        } else {
            setHasChanges(false);
            setOriginalSSNOrEin("");
            setSSNOrEin("");
            setHasInitializedSSNOrEin(false);
            setIdType("SSN");
        }
    }, [checkContractor?.id, isVendor]);

    useEffect(() => {
        (async () => {
            if (checkContractor?.id && checkContractor.onboard?.withholdings && checkContractor.onboard.withholdings.length > 0) {
                await loadForms();
            }
        })();
    }, [checkContractor?.id, checkContractor?.onboard?.withholdings]);

    const loadForms = async () => {
        if (!checkContractor?.id) {
            return;
        }

        setLoadingForms(true);
        
        const apiResponse = await apiCheckListContractorForms(checkContractor.id);
        const formStatuses = apiResponse?.data;
        setCheckResponseError(apiResponse?.checkResponseError);

        if (!apiResponse || isCheckError(apiResponse) || !formStatuses) {
            toast("Error loading forms", toastFail);
            setLoadingForms(false);
            setCheckFormStatuses([]);
            return;
        }

        setCheckFormStatuses(formStatuses);
        setLoadingForms(false);
    };

    const handleOpenForm = async (formStatus: CheckFormStatus) => {
        setSelectedFormStatus(formStatus);
        setLoadingFormDetails(true);
        setShowFormModal(true);

        try {
            const apiResponse = await apiCheckGetContractorForm(checkContractor!.id, formStatus.form.id);
            const theForm = apiResponse?.data;
            setCheckResponseError(apiResponse?.checkResponseError);

            if (!apiResponse || isCheckError(apiResponse) || !theForm) {
                toast("Error loading form details", toastFail);
                setShowFormModal(false);
                return;
            }

            setSelectedForm(theForm);
        } catch (error) {
            console.error("[AppEmployeeRecordPayrollContractor] Error loading form details:", error);
            toast("Error loading form details", toastFail);
            setShowFormModal(false);
        } finally {
            setLoadingFormDetails(false);
        }
    };

    const handleCloseFormModal = () => {
        setShowFormModal(false);
        setSelectedForm(null);
        setSelectedFormStatus(null);
    };

    const formatDateTime = (dateString: string): string => {
        if (!dateString) return "";
        try {
            const date = new Date(dateString);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };

    const isPlaidType = (account: CheckBankAccount): account is Extract<CheckBankAccount, { plaid_bank_account: any }> => {
        return "plaid_bank_account" in account;
    };

    const isRawType = (account: CheckBankAccount): account is Extract<CheckBankAccount, { raw_bank_account: any }> => {
        return "raw_bank_account" in account;
    };

    const getInstitutionName = (account: CheckBankAccount): string => {
        if (isPlaidType(account)) {
            return account.plaid_bank_account.institution_name || "-";
        } else if (isRawType(account)) {
            return account.raw_bank_account.institution_name || "-";
        }
        return "-";
    };

    const getAccountNumber = (account: CheckBankAccount): string => {
        if (isPlaidType(account)) {
            return `****${account.plaid_bank_account.mask}`;
        } else if (isRawType(account)) {
            return `****${account.raw_bank_account.account_number_last_four}`;
        }
        return "-";
    };

    const getSubtype = (account: CheckBankAccount): string => {
        if (isPlaidType(account)) {
            return account.plaid_bank_account.subtype.charAt(0).toUpperCase() + account.plaid_bank_account.subtype.slice(1);
        } else if (isRawType(account)) {
            return account.raw_bank_account.subtype.charAt(0).toUpperCase() + account.raw_bank_account.subtype.slice(1);
        }
        return "-";
    };

    const formatStatus = (status: CheckBankAccountStatus): string => {
        const statusOptions: GenericSelectOption[] = CHECK_BANK_ACCOUNT_STATUS.map((status) => ({
            displayName: status.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" "),
            value: status,
        }));
        const option = statusOptions.find(opt => opt.value === status);
        return option?.displayName || status;
    };

    const loadBankAccounts = async () => {
        if (!checkContractor?.id) return;

        setLoadingBankAccounts(true);
        try {
            const apiResponse = await apiCheckListContractorBankAccounts(checkContractor.id);
            const accounts = apiResponse?.data;

            if (!apiResponse || isCheckError(apiResponse) || !accounts) {
                toast("Error loading bank accounts", toastFail);
                setLoadingBankAccounts(false);
                return;
            }

            setBankAccounts(accounts || []);
            
            if (accounts.length === 1) {
                setSelectedBankAccountId(accounts[0].id);
            }
        } catch (error) {
            console.error("[AppEmployeeRecordPayrollContractor] Error loading bank accounts:", error);
            toast("Error loading bank accounts", toastFail);
        } finally {
            setLoadingBankAccounts(false);
        }
    };

    const handleCreateCheckBankAccount = async (createdBankAccount: CheckBankAccount) => {
        setCheckResponseError(undefined);
        await loadBankAccounts();
        
        if (employeeRecord?.id) {
            const apiResponse = await apiCheckGetContractor(employeeRecord.checkContractorId || "");
            if (apiResponse?.data && onCheckContractorCreated) {
                onCheckContractorCreated(apiResponse.data);
            }
        }
        
        setShowBankAccountForm(false);
        setEditingBankAccount(null);
        
        if (checkContractor) {
            setHasChanges(true);
        }
    };

    const handleUpdateCheckBankAccount = async (updatedBankAccount: CheckBankAccount) => {
        setCheckResponseError(undefined);
        await loadBankAccounts();
        setEditingBankAccount(null);
        setHasChanges(true);
    };

    const handleEditClick = (bankAccount: CheckBankAccount) => {
        if (!isRawType(bankAccount)) {
            return;
        }
        setEditingBankAccount(bankAccount);
        setShowBankAccountForm(true);
    };

    const handleCreateCheckContractor = async () => {
        if (!employeeRecord) {
            toast("Employee record not found", toastFail);
            return;
        }

        // Validate SSN/EIN (9 digits)
        const digits = ssnOrEin.replace(/\D/g, "");
        const currentIdType = isVendor ? "EIN" : idType;
        if (digits.length !== 9) {
            toast(`${currentIdType} must be exactly 9 digits`, toastFail);
            return;
        }

        const checkCompanyId = getCurrentCheckCompanyId(storeUser);
        if (!checkCompanyId) {
            toast("Extended company information not found", toastFail);
            return;
        }

        if (!address) {
            toast("Address is required", toastFail);
            return;
        }

        const startDate = erGetCPValue(employeeRecord, "system-startDate") || "";
        if (!startDate) {
            toast("Start date is required", toastFail);
            return;
        }

        setCreating(true);

        try {
            const workplaceAddress: Address = address;

            const workplacePayload: Omit<CheckWorkplace, "id"> = {
                name: "Primary Workplace",
                address: workplaceAddress,
                company: checkCompanyId,
                active: true,
                metadata: {},
            };

            const apiResponseWorkplace = await apiCheckCreateWorkplace(checkCompanyId, workplacePayload);
            const checkWorkplace = apiResponseWorkplace?.data;
            setCheckResponseError(apiResponseWorkplace?.checkResponseError);

            if (!apiResponseWorkplace || isCheckError(apiResponseWorkplace) || !checkWorkplace) {
                toast("Error creating workplace", toastFail);
                return;
            }

            const firstName = erGetCPValue(employeeRecord, "system-firstName") || "";
            const lastName = erGetCPValue(employeeRecord, "system-lastName") || "";
            const middleName = erGetCPValue(employeeRecord, "system-middleName") || "";
            const email = employeeRecord.workEmail || "";
            const startDateCheck = convertDateToCheckYYYYMMDD(new Date(startDate));

            const currentIdType = isVendor ? "EIN" : idType;
            const checkContractorPayload: Partial<CheckContractor> & { ssn?: string; ein?: string } = {
                type: isVendor ? "business" : "individual",
                first_name: firstName,
                middle_name: middleName || null,
                last_name: lastName,
                email,
                address,
                company: checkCompanyId,                
                start_date: startDateCheck,
                workplaces: [checkWorkplace.id],
                primary_workplace: checkWorkplace.id,
                payment_method_preference: "direct_deposit",
            };

            // Set SSN or EIN based on selection
            if (currentIdType === "EIN") {
                checkContractorPayload.ein = digits;
            } else {
                checkContractorPayload.ssn = digits;
            }

            if (isVendor) {
                checkContractorPayload.business_name = erGetCPValue(employeeRecord, "system-company") || "";
            }

            console.log("[AppEmployeeRecordPayrollContractor] checkContractorPayload: ", checkContractorPayload);
            const apiResponseContractor = await apiCheckCreateContractor(checkCompanyId, checkContractorPayload);
            const newCheckContractor = apiResponseContractor?.data;
            setCheckResponseError(apiResponseContractor?.checkResponseError);

            if (!apiResponseContractor || isCheckError(apiResponseContractor) || !newCheckContractor) {
                toast("Error creating contractor", toastFail);
                return;
            }

            const updatedEmployeeRecord = await apiUpdateEmployeeRecord({
                ...employeeRecord,
                checkContractorId: newCheckContractor.id,
            });
            
            if (!updatedEmployeeRecord) {
                toast("Error updating employee record", toastFail);
                return;
            }

            toast("Contractor enrolled in payroll", toastSuccess);

            if (onCheckContractorCreated) {
                onCheckContractorCreated(newCheckContractor);
            }

        } catch (error) {
            console.error("[AppEmployeeRecordPayrollContractor] Error creating contractor:", error);
            toast("Error creating contractor", toastFail);
        } finally {
            setCreating(false);
        }
    };

    if (!employeeRecord) {
        return (
            <div className="app-employee-record-payroll">
                <Alert variant="warning">
                    <AlertDescription>
                        No employee record found.
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    const allowCreate = !checkContractor;

    const bankAccountOptions: GenericSelectOption[] = bankAccounts.map((account) => ({
        displayName: `${getInstitutionName(account)} - ${getAccountNumber(account)}`,
        value: account.id,
    }));

    const formatAddressDisplay = (address: Address): string => {
        const parts: string[] = [];
        if (address.line1) parts.push(address.line1);
        if (address.line2) parts.push(address.line2);
        const cityStateZip = [address.city, address.state, address.postal_code].filter(Boolean).join(", ");
        if (cityStateZip) parts.push(cityStateZip);
        if (address.country && address.country !== "US") parts.push(address.country);
        return parts.join("\n");
    };

    const isValidSSNOrEin = (value: string): boolean => {
        const digits = value.replace(/\D/g, "");
        return digits.length === 9;
    };

    const canSave = (): boolean => {
        return !!(
            address &&
            isValidSSNOrEin(ssnOrEin)
        );
    };

    const handleSave = async () => {
        if (!canSave()) {
            const currentIdType = isVendor ? "EIN" : idType;
            toast(`Please ensure address and ${currentIdType} are all provided`, toastFail);
            return;
        }

        if (!checkContractor) {
            await handleCreateCheckContractor();
        } else if (hasChanges) {
            const digits = ssnOrEin.replace(/\D/g, "");
            if (digits.length !== 9) {
                const currentIdType = isVendor ? "EIN" : idType;
                toast(`${currentIdType} must be exactly 9 digits`, toastFail);
                return;
            }

            const currentIdType = isVendor ? "EIN" : idType;
            const apiResponse = currentIdType === "EIN" 
                ? await apiCheckUpdateContractorEIN(checkContractor.id, digits) 
                : await apiCheckUpdateContractorSSN(checkContractor.id, digits);
                
            const result = apiResponse?.data;
            setCheckResponseError(apiResponse?.checkResponseError);

            if (!apiResponse || isCheckError(apiResponse) || !result) {
                toast(`Error updating ${currentIdType}`, toastFail);
                return;
            }

            toast(`${currentIdType} updated successfully`, toastSuccess);
            setHasChanges(false);
            if (onCheckContractorUpdated) {
                onCheckContractorUpdated();
            }
        }
    };

    const handleSubmitCheckForm = async (formId: string, parameters: Array<{ name: string; value: string }>) => {
        if (!checkContractor?.id || submittingForm) {
            return;
        }

        setSubmittingForm(true);
        try {
            const apiResponse = await apiCheckSubmitContractorForm(checkContractor.id, formId, parameters);
            const result = apiResponse?.data;
            setCheckResponseError(apiResponse?.checkResponseError);

            if (!apiResponse || isCheckError(apiResponse) || !result) {
                toast("Error submitting form", toastFail);
                return;
            }

            toast("Form submitted successfully", toastSuccess);
            await loadForms();
            handleCloseFormModal();
        } finally {
            setSubmittingForm(false);
        }
    }

    const handleSSNOrEinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let input = e.target.value;
        input = input.replace(/[^\d-]/g, "");
        const digits = input.replace(/-/g, "");
        const limitedDigits = digits.slice(0, 9);
        
        let formatted = "";
        if (limitedDigits.length > 0) {
            const currentIdType = isVendor ? "EIN" : idType;
            if (currentIdType === "EIN") {
                // EIN format: XX-XXXXXXX
                formatted = limitedDigits.slice(0, 2);
                if (limitedDigits.length >= 3) {
                    formatted += "-" + limitedDigits.slice(2, 9);
                }
            } else {
                // SSN format: XXX-XX-XXXX
                formatted = limitedDigits.slice(0, 3);
                if (limitedDigits.length >= 4) {
                    formatted += "-" + limitedDigits.slice(3, 5);
                }
                if (limitedDigits.length >= 6) {
                    formatted += "-" + limitedDigits.slice(5, 9);
                }
            }
        }
        
        setSSNOrEin(formatted);
        if (formatted !== originalSSNOrEin) {
            setHasChanges(true);
        }
    };

    const handleIdTypeChange = (newType: string) => {
        if (!canSwitchIdType) return; // Only US_CONTRACTOR and INT_CONTRACTOR can switch
        setIdType(newType as "SSN" | "EIN");
        setSSNOrEin(""); // Clear the input when switching
        // Reset original value when switching types so hasChanges works correctly
        setOriginalSSNOrEin("");
        setHasChanges(true);
    };

    const idLabel = isVendor ? "EIN" : idType;
    const getPlaceholder = (): string => {
        if (isVendor) {
            return "XX-XXXXXXX";
        }
        if (idType === "EIN") {
            return "XX-XXXXXXX";
        }
        // For SSN: show masked placeholder if business_name is null and ssn_last_four exists
        if (!isVendor && 
            checkContractor?.business_name === null && 
            checkContractor?.ssn_last_four && 
            !ssnInputFocused) {
            return `***-**-${checkContractor.ssn_last_four}`;
        }
        return "XXX-XX-XXXX";
    };
    const idPlaceholder = getPlaceholder();

    const isBlocked = checkContractor?.onboard?.status === "blocking" || checkContractor?.onboard?.status === "needs_attention";

    const hasBasics : boolean = checkContractor &&
        (checkContractor.ssn_last_four !== null || checkContractor.ein !== null) &&
        checkContractor.bank_accounts.length > 0 &&
        checkContractor.workplaces.length > 0 ? true : false;

    console.log("[AppEmployeeRecordPayrollContractor] checkContractor: ", checkContractor);

    return (
        <div className="app-employee-record-payroll">
            <AppCheckContractorStatus 
                checkContractor={checkContractor}
                allowCreate={allowCreate}
                hasBasics={hasBasics}
                handleCreateContractor={handleCreateCheckContractor}
                onViewOnboarding={() => setShowOnboardingStatusModal(true)}
            />

            {checkResponseError && 
                <AppCheckErrorMessage
                    checkResponseError={checkResponseError}
                />
            }

            {checkContractor && 
                <>
                    {showBankAccountForm ? (
                        <AppPayBankAccount
                            bankAccount={editingBankAccount}
                            checkContractorId={checkContractor.id}
                            onClose={() => {
                                setShowBankAccountForm(false);
                                setEditingBankAccount(null);
                            }}
                            onUpdate={handleUpdateCheckBankAccount}
                            onCreate={handleCreateCheckBankAccount}
                            onError={setCheckResponseError}
                        />
                    ) : (
                        <>
                            {/* <div style={{ marginBottom: "24px" }}>
                                <div className="app-custom-property-label" style={{ marginBottom: "8px" }}>
                                    Address
                                </div>
                                {address ? (
                                    <div className="app-custom-property-value">
                                        <div style={{ 
                                            padding: "12px", 
                                            backgroundColor: "#f5f5f5", 
                                            borderRadius: "4px",
                                            whiteSpace: "pre-line",
                                            fontFamily: "monospace",
                                            fontSize: "14px"
                                        }}>
                                            {address.line1}
                                            {address.line2 && <br />}
                                            {address.line2 && <span style={{ fontWeight: "bold" }}>{address.line2}</span>}
                                            {address.city && <br />}
                                            {address.city && <span style={{ fontWeight: "bold" }}>{address.city}</span>}
                                            {address.state && <br />}
                                            {address.state && <span style={{ fontWeight: "bold" }}>{address.state}</span>}
                                            {address.postal_code && <br />}
                                            {address.postal_code && <span style={{ fontWeight: "bold" }}>{address.postal_code}</span>}
                                        </div>
                                    </div>
                                ) : (
                                    <Alert variant="warning" className="app-pay-configuration-alert">
                                        <AlertDescription>
                                            Address needs to be entered in the Profile tab.
                                        </AlertDescription>
                                    </Alert>
                                )}
                            </div> */}

                           
                            <div style={{ marginBottom: "24px" }}>
                                {/* <Alert variant="destructive" className="app-pay-configuration-alert" style={{ marginBottom: "16px" }}>
                                    <AlertDescription>
                                        <strong>{idLabel} Required:</strong> A new {idLabel} must be entered to complete contractor onboarding.
                                    </AlertDescription>
                                </Alert> */}
                                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    {canSwitchIdType && (
                                        <div style={{ marginBottom: "8px" }}>
                                            <div className="app-custom-property-label" style={{ marginBottom: "8px" }}>
                                                Identification Type
                                            </div>
                                            <Radio
                                                name="id-type"
                                                choices={["SSN", "EIN"]}
                                                choice={idType}
                                                onChange={handleIdTypeChange}
                                            />
                                        </div>
                                    )}
                                    <div className="app-custom-property-label">
                                        {idLabel} *
                                    </div>
                                    <div className="app-custom-property-value">
                                        <Input
                                            type="text"
                                            placeholder={idPlaceholder}
                                            value={ssnOrEin}
                                            onChange={handleSSNOrEinChange}
                                            onFocus={() => setSSNInputFocused(true)}
                                            onBlur={() => setSSNInputFocused(false)}
                                            maxLength={11}
                                        />
                                    </div>
                                </div>
                            </div>
                            

                            <div className="app-employee-payroll-bank-accounts-header">
                                <h3>Bank Accounts</h3>
                                <ActionLink
                                    label="Add Bank Account"
                                    leftIcon={<Plus size={16} />}
                                    onClick={() => {
                                        setEditingBankAccount(null);
                                        setShowBankAccountForm(true);
                                    }}
                                />
                            </div>

                            {loadingBankAccounts ? (
                                <div className="app-employee-payroll-loading">
                                    Loading bank accounts...
                                </div>
                            ) : (!checkContractor || (checkContractor && checkContractor.bank_accounts.length === 0)) && bankAccounts.length === 0 ? (
                                <Alert variant="warning" className="app-pay-configuration-alert">
                                    <AlertDescription>
                                        No bank accounts found. Click "Add Bank Account" to create one.
                                    </AlertDescription>
                                </Alert>
                            ) : (
                                <>
                                    {bankAccounts.length > 1 && (
                                        <div className="app-employee-payroll-bank-account-select">
                                            <div className="app-custom-property-label">
                                                Select Bank Account
                                            </div>
                                            <div className="app-custom-property-value">
                                                <GenericSelect
                                                    options={bankAccountOptions}
                                                    selected={bankAccountOptions.find(opt => opt.value === selectedBankAccountId)}
                                                    onChange={(value: any) => setSelectedBankAccountId(value as string)}
                                                    maxHeight="200px"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="app-pay-bank-accounts-table-container">
                                        <table className="app-pay-bank-accounts-table">
                                            <thead>
                                                <tr>
                                                    <th>Institution</th>
                                                    <th>Account Number</th>
                                                    <th>Type</th>
                                                    <th>Status</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {bankAccounts.map((bankAccount) => (
                                                    <tr 
                                                        key={bankAccount.id} 
                                                        className={`app-pay-bank-accounts-row ${selectedBankAccountId === bankAccount.id ? 'app-pay-bank-accounts-row-selected' : ''}`}
                                                    >
                                                        <td className="app-pay-bank-accounts-institution-cell">
                                                            <strong>{getInstitutionName(bankAccount)}</strong>
                                                        </td>
                                                        <td className="app-pay-bank-accounts-account-cell">
                                                            {getAccountNumber(bankAccount)}
                                                        </td>
                                                        <td className="app-pay-bank-accounts-type-cell">
                                                            {getSubtype(bankAccount)}
                                                        </td>
                                                        <td className="app-pay-bank-accounts-status-cell">
                                                            {formatStatus(bankAccount.status)}
                                                        </td>
                                                        <td className="app-pay-bank-accounts-actions-cell">
                                                            <Icon
                                                                variant="tertiary"
                                                                size="md"
                                                                icon={<Edit size={16} />}
                                                                onClick={() => handleEditClick(bankAccount)}
                                                                title={isRawType(bankAccount) ? "Edit" : "You can not edit Plaid bank accounts"}
                                                                disabled={!isRawType(bankAccount)}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </>
            }

            {!checkContractor && checkCompanyId && 
                <>
                    <div className="app-employee-payroll-bank-accounts-header">
                        <h3>Payroll Setup</h3>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "24px" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <div className="app-custom-property-label">
                                Address
                            </div>
                            {address ? (
                                <div className="app-custom-property-value">
                                    <div style={{ 
                                        padding: "12px", 
                                        backgroundColor: "#f5f5f5", 
                                        borderRadius: "4px",
                                        whiteSpace: "pre-line",
                                        fontFamily: "monospace",
                                        fontSize: "14px"
                                    }}>
                                        {formatAddressDisplay(address)}
                                    </div>
                                </div>
                            ) : (
                                <Alert variant="warning" className="app-pay-configuration-alert">
                                    <AlertDescription>
                                        Address needs to be entered in the Profile tab.
                                    </AlertDescription>
                                </Alert>
                            )}
                        </div>
                        <div className="app-employee-payroll-form">
                            <div className="app-employee-payroll-form-header">
                                <h4>Bank Account</h4>
                            </div>
                            <div className="app-employee-payroll-form-content">
                                <Alert variant="default" className="app-pay-configuration-alert">
                                    <AlertDescription>
                                        Add a bank account for direct deposit payments.
                                    </AlertDescription>
                                </Alert>
                            </div>
                            <div className="app-employee-payroll-form-actions">
                                <Alert variant="default" className="app-pay-configuration-alert">
                                    <AlertDescription>
                                        Bank accounts can be added after enrolling the contractor in payroll.
                                    </AlertDescription>
                                </Alert>
                            </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            {canSwitchIdType && (
                                <div style={{ marginBottom: "8px" }}>
                                    <div className="app-custom-property-label" style={{ marginBottom: "8px" }}>
                                        Identification Type
                                    </div>
                                    <Radio
                                        name="id-type-create"
                                        choices={["SSN", "EIN"]}
                                        choice={idType}
                                        onChange={handleIdTypeChange}
                                    />
                                </div>
                            )}
                            <div className="app-custom-property-label">
                                {idLabel}
                            </div>
                            <div className="app-custom-property-value">
                                <Input
                                    type="text"
                                    placeholder={idPlaceholder}
                                    value={ssnOrEin}
                                    onChange={handleSSNOrEinChange}
                                    onFocus={() => setSSNInputFocused(true)}
                                    onBlur={() => setSSNInputFocused(false)}
                                    maxLength={11}
                                />
                            </div>
                        </div>
                    </div>
                    
                    {!checkContractor && (
                        <div style={{ 
                            display: "flex", 
                            justifyContent: "flex-end", 
                            marginTop: "24px",
                            paddingTop: "24px",
                            borderTop: "1px solid #e5e5e5"
                        }}>
                            <Button
                                label="Enroll"
                                variant="primary"
                                onClick={handleSave}
                                disabled={creating || loadingBankAccounts || !canSave()}
                            />
                        </div>
                    )}
                </>
            }

            {checkContractor && (checkContractor.onboard?.withholdings && checkContractor.onboard.withholdings.length > 0) && (
                <div style={{ marginTop: "32px" }}>
                    <h3 style={{ marginBottom: "24px" }}>Tax Forms and Withholdings</h3>
                    {loadingForms ? (
                        <div className="app-employee-payroll-loading">
                            Loading required withholding forms...
                        </div>
                    ) : checkFormStatuses.length > 0 ? (
                        <div className="app-pay-bank-accounts-table-container">
                            <table className="app-pay-bank-accounts-table">
                                <thead>
                                    <tr>
                                        <th>Description</th>
                                        <th>Status</th>
                                        <th>Submitted</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {checkFormStatuses.map((formStatus: CheckFormStatus) => {
                                        const isSubmitted = !!formStatus.submitted_at;
                                        return (
                                            <tr 
                                                key={formStatus.form.id}
                                                className="app-pay-bank-accounts-row"
                                            >
                                                <td>
                                                    <strong>{formStatus.form.description}</strong>
                                                </td>
                                                <td>
                                                    <span className={isSubmitted ? "blocking-badge non-blocking" : "blocking-badge blocking"}>
                                                        {isSubmitted ? "Submitted" : "Not Submitted"}
                                                    </span>
                                                </td>
                                                <td>
                                                    {isSubmitted ? formatDateTime(formStatus.submitted_at) : "-"}
                                                </td>
                                                <td>
                                                    <Button
                                                        label={isSubmitted ? "Update" : "Submit"}
                                                        variant="secondary"
                                                        onClick={() => handleOpenForm(formStatus)}
                                                        disabled={submittingForm || loadingFormDetails}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <Alert variant="default" className="app-pay-configuration-alert">
                            <AlertDescription>
                                No withholding forms found.
                            </AlertDescription>
                        </Alert>
                    )}
                </div>
            )}
            
            {checkContractor && !showBankAccountForm && hasChanges && (
                <div style={{ 
                    display: "flex", 
                    justifyContent: "flex-end", 
                    marginTop: "24px",
                    paddingTop: "24px",
                    borderTop: "1px solid #e5e5e5"
                }}>
                    <Button
                        label="Update"
                        variant="primary"
                        onClick={handleSave}
                        disabled={creating || loadingBankAccounts || !isValidSSNOrEin(ssnOrEin)}
                    />
                </div>
            )}

            {checkContractor && 
                <ModalComponentWrapper
                    isOpen={showOnboardingStatusModal}
                    onClose={() => setShowOnboardingStatusModal(false)}
                    style={{ width: "90%", maxWidth: "900px", maxHeight: "90vh", overflow: "auto" }}
                >
                    <div style={{ padding: "24px" }}>
                        <AppPayContractorOnboardingStatus 
                            checkContractor={checkContractor}
                            showInModal={true}
                            onResetSSN={async () => {
                                if (!checkContractor?.id) return;
                                
                                // Clear SSN by updating with empty string
                                // This will allow the user to enter a new SSN
                                const apiResponse = await apiCheckUpdateContractorSSN(checkContractor.id, "");
                                setCheckResponseError(apiResponse?.checkResponseError);
                                
                                if (apiResponse && !isCheckError(apiResponse)) {
                                    toast("SSN cleared. Please enter a new SSN.", toastSuccess);
                                    if (onCheckContractorUpdated) {
                                        onCheckContractorUpdated();
                                    }
                                } else {
                                    toast("Error clearing SSN", toastFail);
                                }
                            }}
                        />
                    </div>
                </ModalComponentWrapper>
            }


            {/* {checkContractor && (
                <ModalComponentWrapper
                    isOpen={showFormModal}
                    onClose={handleCloseFormModal}
                    style={{ width: "90%", maxWidth: "900px", maxHeight: "90vh", overflow: "auto" }}
                >
                    <div style={{ padding: "24px" }}>
                        {loadingFormDetails ? (
                            <div className="app-employee-payroll-loading">
                                Loading form details...
                            </div>
                        ) : selectedForm && selectedFormStatus ? (
                            <AppEmployeeRecordCheckForm
                                checkForm={selectedForm}
                                showTitle={true}
                                disabled={submittingForm}
                                isSubmitted={!!selectedFormStatus.submitted_at}
                                onSubmit={(parameters) => {
                                    handleSubmitCheckForm(selectedForm.id, parameters);
                                }}
                            />
                        ) : (
                            <Alert variant="warning">
                                <AlertDescription>
                                    Form not found.
                                </AlertDescription>
                            </Alert>
                        )}
                    </div>
                </ModalComponentWrapper>
            )} */}
        </div>
    );
};

